import { useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Activity, Key, Globe, Settings, LogOut, Terminal, Download, ShieldAlert, AlertTriangle, Flame, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const NAV = [
    { to: "/app/tunnels", label: "Tunnels", icon: Activity, testid: "nav-tunnels" },
    { to: "/app/keys", label: "API Keys", icon: Key, testid: "nav-keys" },
    { to: "/app/domains", label: "Custom Domains", icon: Globe, testid: "nav-domains" },
    { to: "/app/cli", label: "CLI Install", icon: Download, testid: "nav-cli" },
    { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function DashLayout({ children, title, subtitle, action }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [upgrading, setUpgrading] = useState(false);

    const onLogout = async () => {
        await logout();
        navigate("/");
    };

    const handleUpgrade = async (planId) => {
        setUpgrading(planId);
        try {
            const { data } = await api.post("/auth/billing/checkout", { plan_id: planId });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error("Failed to generate upgrade checkout link");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to initialize upgrade");
        } finally {
            setUpgrading(false);
        }
    };

    const isSettingsPage = location.pathname === "/app/settings";
    const trialExpired = user?.trial_expired;
    const showBlocker = trialExpired && !isSettingsPage && user?.role !== "admin";

    const getTrialStatusText = () => {
        if (user?.role === "admin") return null;
        if (user?.plan === "pro" || user?.plan === "enterprise") return null;
        if (!user?.trial_end) return null;
        
        try {
            const end = new Date(user.trial_end);
            const now = new Date();
            const diffTime = end - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays <= 0 || user.trial_expired) {
                return <span className="text-[9px] uppercase tracking-widest text-[#FF3B30] font-bold block mt-1">TRIAL EXPIRED</span>;
            }
            return <span className="text-[9px] uppercase tracking-widest text-[#00FF41] block mt-1">{diffDays} DAYS LEFT ON TRIAL</span>;
        } catch {
            return null;
        }
    };

    const renderContent = () => {
        if (showBlocker) {
            return (
                <div className="max-w-3xl mx-auto py-12" data-testid="trial-expired-blocker">
                    <div className="tf-card-accent border border-[#27272A] p-8 md:p-12 relative overflow-hidden">
                        <div className="scanlines absolute inset-0 pointer-events-none opacity-[0.05]" />
                        
                        <div className="flex items-center gap-3 text-[#FF9900] mb-6">
                            <AlertTriangle className="w-8 h-8 animate-pulse" />
                            <div>
                                <div className="label-mono text-[#FF9900]">// subscription status</div>
                                <h2 className="font-display text-2xl font-black uppercase tracking-tight text-[#FAFAFA]">FREE TRIAL EXPIRED</h2>
                            </div>
                        </div>

                        <p className="text-sm text-[#A1A1AA] font-mono leading-relaxed mb-8">
                            Your trial period has ended. To continue using SynapseLink tunneling, persistent subdomains, password protection, and custom domains, please upgrade to a developer plan.
                        </p>

                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                            {/* Pro Plan Card */}
                            <div className="border border-[#27272A] bg-[#050505] p-6 flex flex-col justify-between hover:border-[#00FF41] transition-colors">
                                <div>
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-xs font-bold text-[#00FF41] uppercase tracking-wider">Developer Pro</span>
                                        <Flame className="w-4 h-4 text-[#00FF41]" />
                                    </div>
                                    <div className="font-display text-2xl font-bold text-[#FAFAFA] mb-4">
                                        $9 <span className="text-xs text-[#52525B] font-mono">/ mo</span>
                                    </div>
                                    <ul className="space-y-2 text-[11px] text-[#A1A1AA] mb-6">
                                        <li>• 5 Active Tunnels</li>
                                        <li>• Custom Subdomains</li>
                                        <li>• 1 Custom Domain</li>
                                        <li>• Password Protection</li>
                                        <li>• 50,000 reqs / day</li>
                                    </ul>
                                </div>
                                <button
                                    disabled={upgrading !== false}
                                    onClick={() => handleUpgrade("pro_plan")}
                                    className="tf-btn w-full justify-center text-center text-xs"
                                >
                                    {upgrading === "pro_plan" ? "INITIALIZING..." : "UPGRADE TO PRO"}
                                </button>
                            </div>

                            {/* Enterprise Plan Card */}
                            <div className="border border-[#27272A] bg-[#050505] p-6 flex flex-col justify-between hover:border-[#FF9900] transition-colors">
                                <div>
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-xs font-bold text-[#FF9900] uppercase tracking-wider">Enterprise</span>
                                        <ShieldAlert className="w-4 h-4 text-[#FF9900]" />
                                    </div>
                                    <div className="font-display text-2xl font-bold text-[#FAFAFA] mb-4">
                                        $29 <span className="text-xs text-[#52525B] font-mono">/ mo</span>
                                    </div>
                                    <ul className="space-y-2 text-[11px] text-[#A1A1AA] mb-6">
                                        <li>• 100 Active Tunnels</li>
                                        <li>• Custom Subdomains</li>
                                        <li>• Unlimited Custom Domains</li>
                                        <li>• Password Protection</li>
                                        <li>• 5,000,000 reqs / day</li>
                                    </ul>
                                </div>
                                <button
                                    disabled={upgrading !== false}
                                    onClick={() => handleUpgrade("enterprise_plan")}
                                    className="tf-btn tf-btn-ghost !border-[#FF9900] !text-[#FF9900] hover:!bg-[#FF9900] hover:!text-black w-full justify-center text-center text-xs"
                                >
                                    {upgrading === "enterprise_plan" ? "INITIALIZING..." : "UPGRADE TO ENTERPRISE"}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-between items-center border-t border-[#27272A] pt-6 text-xs font-mono text-[#52525B]">
                            <span>Trial Start: {user?.trial_start ? new Date(user.trial_start).toLocaleDateString() : "—"}</span>
                            <span>Trial End: {user?.trial_end ? new Date(user.trial_end).toLocaleDateString() : "—"}</span>
                        </div>
                    </div>
                </div>
            );
        }
        return children;
    };

    return (
        <div className="min-h-screen bg-[#050505] text-[#FAFAFA] flex">
            <aside className="w-[260px] shrink-0 border-r border-[#27272A] bg-[#0A0A0A] flex flex-col" data-testid="sidebar">
                <Link to="/app/tunnels" className="flex items-center gap-2 px-5 py-5 border-b border-[#27272A]" data-testid="sidebar-logo">
                    <Terminal className="w-5 h-5 text-[#00FF41]" strokeWidth={2.5} />
                    <span className="font-display text-xl tracking-tight uppercase font-bold">SYNAPSELINK</span>
                </Link>
                <nav className="flex-1 py-4">
                    {NAV.map(({ to, label, icon: Icon, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-5 py-3 text-sm uppercase tracking-wider border-l-2 ${
                                    isActive ? "border-[#00FF41] text-[#00FF41] bg-[#0F0F0F]" : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA] hover:bg-[#0F0F0F]"
                                }`
                            }
                        >
                            <Icon className="w-4 h-4" strokeWidth={2} />
                            {label}
                        </NavLink>
                    ))}
                    {user?.role === "admin" && (
                        <NavLink
                            to="/app/admin"
                            data-testid="nav-admin"
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-5 py-3 text-sm uppercase tracking-wider border-l-2 ${
                                    isActive ? "border-[#FF9900] text-[#FF9900] bg-[#0F0F0F]" : "border-transparent text-[#FF9900] hover:text-[#FAFAFA] hover:bg-[#0F0F0F]"
                                }`
                            }
                        >
                            <ShieldAlert className="w-4 h-4 animate-pulse" strokeWidth={2} />
                            Admin Panel
                        </NavLink>
                    )}
                </nav>
                <div className="border-t border-[#27272A] p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#52525B]">Signed in</div>
                    <div className="text-sm text-[#FAFAFA] truncate" data-testid="sidebar-user-email">{user?.email}</div>
                    <div className="flex flex-col mt-1">
                        <span className="text-[10px] uppercase tracking-widest text-[#FF9900]">{(user?.plan || "free").toUpperCase()} TIER</span>
                        {getTrialStatusText()}
                    </div>
                    <button onClick={onLogout} data-testid="logout-btn" className="mt-3 w-full flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-[#A1A1AA] hover:text-[#FF3B30] border border-[#27272A] hover:border-[#FF3B30] py-2 transition-colors">
                        <LogOut className="w-3.5 h-3.5" /> Logout
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0 flex flex-col">
                {user && !user.email_verified && (
                    <div className="bg-[#FF9900]/10 border-b border-[#FF9900]/30 text-[#FF9900] px-8 py-3 text-xs font-mono flex items-center justify-between" data-testid="unverified-banner">
                        <span>⚠️ Account unverified. Please check your email to activate your account and start tunneling.</span>
                    </div>
                )}
                <header className="border-b border-[#27272A] px-8 py-6 flex items-end justify-between gap-4 bg-[#050505]" data-testid="page-header">
                    <div>
                        <div className="label-mono mb-2">// {subtitle || "Console"}</div>
                        <h1 className="heading-lg text-[#FAFAFA]">{title}</h1>
                    </div>
                    {!showBlocker && action}
                </header>
                <div className="flex-1 p-8">
                    {renderContent()}
                </div>
            </main>
        </div>
    );
}
